/**
 * FounsiItemRow — horizontal scroll of inventory cards inside an
 * assistant bubble.
 *
 * Renders when a tool returned navigable inventory items
 * (see `parseItemsFromToolResult`).  Tapping a card opens the same
 * `ItemDetail` screen Browse links into.
 *
 * Empty list is filtered upstream — this component assumes
 * `items.length > 0`.
 */

import React from 'react';
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ItemThumbnail } from '../../../components/ItemThumbnail';
import { fpColors } from '../../../styles/theme';
import type { FounsiItem } from '../api/parseItemsFromToolResult';

interface Props {
  items: FounsiItem[];
}

// Mobile-friendly card sizes — wide enough that the title rarely
// hits a 2-line wrap on common item names, narrow enough that 3
// cards peek across a 375px viewport (the third card is partially
// visible as the affordance for "scroll to see more").
const CARD_WIDTH = 124;
const THUMB_SIZE = 108;

export const FounsiItemRow: React.FC<Props> = ({ items }) => {
  const navigation = useNavigation<any>();

  if (!items.length) return null;

  return (
    <View style={styles.wrap} testID="founsi-item-row">
      <FlatList
        data={items}
        horizontal
        showsHorizontalScrollIndicator={false}
        keyExtractor={(it) => `${it.inventory_item_id}`}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.listContent}
        renderItem={({ item }) => (
          <Pressable
            style={({ pressed }) => [
              styles.card,
              pressed && styles.cardPressed,
            ]}
            onPress={() =>
              navigation.navigate('ItemDetail', {
                itemId: item.inventory_item_id,
              })
            }
            accessibilityRole="button"
            accessibilityLabel={`Open ${item.item_name}`}
            testID={`founsi-item-card-${item.inventory_item_id}`}
          >
            <ItemThumbnail
              item={{ source_image_url: item.source_image_url ?? null }}
              size={THUMB_SIZE}
              placeholderIcon="inventory"
            />
            <Text style={styles.title} numberOfLines={2}>
              {item.item_name}
            </Text>
            {item.sub_label ? (
              <Text style={styles.subtitle} numberOfLines={1}>
                {item.sub_label}
              </Text>
            ) : null}
          </Pressable>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    marginBottom: 8,
    marginHorizontal: -10, // bleed into the bubble padding for breathing room
  },
  listContent: {
    paddingHorizontal: 10,
    gap: 8,
  },
  card: {
    width: CARD_WIDTH,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    padding: 6,
  },
  cardPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  title: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.onSurface,
    lineHeight: 15,
  },
  subtitle: {
    marginTop: 2,
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
});

export default FounsiItemRow;
